import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { ProductPromotion } from '../../lib/types';
import { directusOperations } from '../../lib/directus';

interface ProductPromotionsState {
  productPromotions: ProductPromotion[];
  isLoading: boolean;
  error: string | null;
  lastFetched: string | null;
  cacheTTL: number; // 30 minutes
}

const initialState: ProductPromotionsState = {
  productPromotions: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  cacheTTL: 30 * 60 * 1000, // 30 minutes
};

/**
 * Fetch all active product promotions
 */
export const fetchProductPromotions = createAsyncThunk(
  'productPromotions/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await directusOperations.getProductPromotions();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch product promotions');
    }
  }
);

/**
 * Fetch product promotion by ID
 */
export const fetchProductPromotionById = createAsyncThunk(
  'productPromotions/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await directusOperations.getProductPromotionById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch product promotion');
    }
  }
);

/**
 * Fetch product promotions by priority level
 */
export const fetchProductPromotionsByPriority = createAsyncThunk(
  'productPromotions/fetchByPriority',
  async (priorityLevel: string, { rejectWithValue }) => {
    try {
      const response = await directusOperations.getProductPromotionsByPriority(priorityLevel);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch product promotions by priority');
    }
  }
);

const productPromotionsSlice = createSlice({
  name: 'productPromotions',
  initialState,
  reducers: {
    clearProductPromotions: (state) => {
      state.productPromotions = [];
      state.lastFetched = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all product promotions
    builder.addCase(fetchProductPromotions.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProductPromotions.fulfilled, (state, action: PayloadAction<ProductPromotion[]>) => {
      state.isLoading = false;
      state.productPromotions = action.payload;
      state.lastFetched = new Date().toISOString();
    });
    builder.addCase(fetchProductPromotions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch product promotion by ID
    builder.addCase(fetchProductPromotionById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProductPromotionById.fulfilled, (state, action: PayloadAction<ProductPromotion>) => {
      state.isLoading = false;
      // Update or add the promotion to the list
      const index = state.productPromotions.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.productPromotions[index] = action.payload;
      } else {
        state.productPromotions.push(action.payload);
      }
    });
    builder.addCase(fetchProductPromotionById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch product promotions by priority
    builder.addCase(fetchProductPromotionsByPriority.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProductPromotionsByPriority.fulfilled, (state, action: PayloadAction<ProductPromotion[]>) => {
      state.isLoading = false;
      // Merge results with existing promotions
      action.payload.forEach(promo => {
        const index = state.productPromotions.findIndex(p => p.id === promo.id);
        if (index !== -1) {
          state.productPromotions[index] = promo;
        } else {
          state.productPromotions.push(promo);
        }
      });
    });
    builder.addCase(fetchProductPromotionsByPriority.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

// Selectors
export const selectProductPromotions = (state: RootState) => state.productPromotions.productPromotions;
export const selectProductPromotionsLoading = (state: RootState) => state.productPromotions.isLoading;
export const selectProductPromotionsError = (state: RootState) => state.productPromotions.error;
export const selectProductPromotionsLastFetched = (state: RootState) => state.productPromotions.lastFetched;

/**
 * Get promotions by priority level
 */
export const selectProductPromotionsByPriority = (priorityLevel: string) => (state: RootState) => {
  return state.productPromotions.productPromotions.filter(
    promo => promo.priority_level === priorityLevel && promo.status === 'active'
  );
};

/**
 * Get active promotions sorted by priority
 */
export const selectActivePromotionsSorted = createSelector(
  [(state: RootState) => state.productPromotions.productPromotions],
  (promotions) => {
    return promotions
      .filter(promo => promo.status === 'active')
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }
);

/**
 * Check if cache is still valid
 */
export const selectIsCacheValid = (state: RootState): boolean => {
  if (!state.productPromotions.lastFetched) return false;
  const lastFetched = new Date(state.productPromotions.lastFetched).getTime();
  const now = Date.now();
  return (now - lastFetched) < state.productPromotions.cacheTTL;
};

export const { clearProductPromotions, clearError } = productPromotionsSlice.actions;
export default productPromotionsSlice.reducer;
